/**
 * Page détail/modification d'un Chapitre
 *
 * - Mode lecture : affiche les détails + liste des leçons
 * - Mode édition : formulaire inline
 * - Navigation vers leçons
 */

import React, { useState, useCallback } from 'react';
import { useEnvironment } from '../../contexts/EnvironmentContext';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Breadcrumbs,
  Link,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Divider,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Add, Edit, Home, Close } from '@mui/icons-material';
import { Chapitre } from '../../types';
import ChapitresLeconsApiService from '../../services/chapitresLeconsApi';
import CurriculumApiService from '../../services/curriculumApi';
import ChapitreForm from './ChapitreForm';
import ChapitreDetail from './ChapitreDetail';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useRBAC } from '../../contexts/RBACContext';
import { useDeleteLecon } from '../../hooks/useData';
import { useNotify } from '../../contexts/NotificationContext';

const ChapitreView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // Seul un admin modifie le contenu.
  const { isAdmin } = useRBAC();
  const peutModifier = isAdmin();

  const [isEditing, setIsEditing] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [leconToDelete, setLeconToDelete] = useState<string | null>(null);
  const { notify } = useNotify('content');
  const { environment: env } = useEnvironment();

  const deleteLeconMutation = useDeleteLecon();

  // Charger le chapitre
  const { data: chapitre, isLoading: loadingChapitre } = useQuery({
    queryKey: ['chapitre', id, env],
    queryFn: () => ChapitresLeconsApiService.getChapitreById(env, id!),
    enabled: !!id
  });

  // Charger la matière parente
  const { data: matiere } = useQuery({
    queryKey: ['matiere', chapitre?.matiereId, env],
    queryFn: () => CurriculumApiService.getMatiereById(env, chapitre!.matiereId!),
    enabled: !!chapitre?.matiereId
  });

  // Charger la classe parente
  const { data: classe } = useQuery({
    queryKey: ['classe', matiere?.classeId, env],
    queryFn: () => CurriculumApiService.getClasseById(env, matiere!.classeId!),
    enabled: !!matiere?.classeId
  });

  // Charger les leçons de ce chapitre
  const { data: allLecons = [] } = useQuery({
    queryKey: ['lecons', env],
    queryFn: () => ChapitresLeconsApiService.getLecons(env)
  });

  const lecons = allLecons.filter(l => l.chapitreId === id);

  const handleSave = useCallback(async (data: Omit<Chapitre, 'id'>) => {
    if (!id) return;

    try {
      await ChapitresLeconsApiService.updateChapitre(env, id, data);
      notify('Chapitre mis à jour avec succès', 'success');
      setIsEditing(false);
      // Invalider le cache
      queryClient.invalidateQueries({ queryKey: ['chapitre', id] });
      queryClient.invalidateQueries({ queryKey: ['chapitres'] });
    } catch {
      notify('Erreur lors de la sauvegarde', 'error');
    }
  }, [id, env, queryClient, notify]);

  const handleDeleteLecon = async (leconId: string) => {
    try {
      await deleteLeconMutation.mutateAsync({ id: leconId, env });
      notify('Leçon supprimée avec succès', 'success');
    } catch {
      notify('Erreur lors de la suppression', 'error');
    }
    setOpenDelete(false);
    setLeconToDelete(null);
  };

  const handleLeconClick = (leconId: string) => {
    navigate(`/lecons/${leconId}`);
  };

  const columns: GridColDef[] = [
    {
      field: 'titre',
      headerName: 'Titre',
      width: 250,
      renderCell: (params) => (
        <Link
          component="button"
          variant="body2"
          onClick={() => handleLeconClick(params.row.id)}
          sx={{ cursor: 'pointer', textAlign: 'left' }}
        >
          {params.value}
        </Link>
      )
    },
    {
      field: 'ordre',
      headerName: 'Ordre',
      width: 80
    },
    {
      field: 'niveauDifficulte',
      headerName: 'Niveau',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value || 'N/A'}
          color={params.value === 'facile' ? 'success' : params.value === 'moyen' ? 'warning' : 'error'}
          size="small"
        />
      )
    },
    {
      field: 'tempsEstimeMinutes',
      headerName: 'Durée (min)',
      width: 120
    },
    {
      field: 'statut',
      headerName: 'Statut',
      width: 120,
      renderCell: (params) => {
        const statut = params.value || 'published';
        return (
          <Chip
            label={statut === 'draft' ? 'Brouillon' : 'Publié'}
            color={statut === 'draft' ? 'warning' : 'success'}
            size="small"
          />
        );
      }
    },
    {
      field: 'estActif',
      headerName: 'Actif',
      width: 80,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'Oui' : 'Non'}
          color={params.value ? 'success' : 'default'}
          size="small"
        />
      )
    }
  ];

  if (loadingChapitre) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!chapitre) {
    return (
      <Box textAlign="center" mt={4}>
        <Typography variant="h5">Chapitre non trouvé</Typography>
        <Button onClick={() => navigate('/chapitres')} sx={{ mt: 2 }}>
          Retour aux chapitres
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Breadcrumb */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link
          component="button"
          variant="body1"
          onClick={() => navigate('/')}
          sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
        >
          <Home sx={{ mr: 0.5 }} fontSize="small" />
          Accueil
        </Link>
        <Link
          component="button"
          variant="body1"
          onClick={() => navigate('/chapitres')}
          sx={{ cursor: 'pointer' }}
        >
          Chapitres
        </Link>
        {classe && (
          <Link
            component="button"
            variant="body1"
            onClick={() => navigate(`/classes/${classe.id}`)}
            sx={{ cursor: 'pointer' }}
          >
            {classe.nom}
          </Link>
        )}
        {matiere && (
          <Link
            component="button"
            variant="body1"
            onClick={() => navigate(`/matieres/${matiere.id}`)}
            sx={{ cursor: 'pointer' }}
          >
            {matiere.nom}
          </Link>
        )}
        <Typography color="text.primary">{chapitre.titre}</Typography>
      </Breadcrumbs>

      {/* Informations du chapitre */}
      <Paper sx={{ mb: 3, p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Typography variant="h5" component="h1">
            {chapitre.titre}
          </Typography>
          {peutModifier && !isEditing && (
            <Button
              variant="contained"
              startIcon={<Edit />}
              onClick={() => setIsEditing(true)}
            >
              Modifier
            </Button>
          )}
          {isEditing && (
            <Button
              variant="outlined"
              startIcon={<Close />}
              onClick={() => setIsEditing(false)}
            >
              Annuler
            </Button>
          )}
        </Box>

        {isEditing ? (
          <ChapitreForm
            chapitre={chapitre}
            defaultMatiereId={chapitre.matiereId}
            onSubmit={handleSave}
            onCancel={() => setIsEditing(false)}
          />
        ) : (
          <ChapitreDetail chapitre={chapitre} />
        )}
      </Paper>

      <Divider sx={{ my: 3 }} />

      {/* Liste des leçons */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5">
          Leçons de ce chapitre ({lecons.length})
        </Typography>
        {peutModifier && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => navigate(`/lecons/new?chapitreId=${id}`)}
          >
            Nouvelle Leçon
          </Button>
        )}
      </Box>

      <Paper sx={{ height: 500, width: '100%' }}>
        <DataGrid
          rows={lecons}
          columns={columns}
          pageSizeOptions={[10, 25, 50]}
          initialState={{
            pagination: { paginationModel: { pageSize: 10 } },
          }}
          disableRowSelectionOnClick
        />
      </Paper>

      {/* Dialog de suppression */}
      {peutModifier && (
        <Dialog open={openDelete} onClose={() => setOpenDelete(false)}>
          <DialogTitle>Confirmer la suppression</DialogTitle>
          <DialogContent>
            <Typography>
              Êtes-vous sûr de vouloir supprimer cette leçon ? Cette action est irréversible.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDelete(false)}>Annuler</Button>
            <Button
              onClick={() => leconToDelete && handleDeleteLecon(leconToDelete)}
              color="error"
              variant="contained"
            >
              Supprimer
            </Button>
          </DialogActions>
        </Dialog>
      )}

    </Box>
  );
};

export default ChapitreView;
